import {ofType} from 'redux-observable';
import {
  catchError,
  map,
  switchMap,
  debounceTime,
  delay,
  mergeMap,
} from 'rxjs/operators';
import {merge} from 'immutable';

import {buildRoute} from '../../http';
import {ActionCreator, buildCatchError, createReducer} from '../../store/utils';
import {RootState} from '..';

import {
  endpoints,
  getOrderApi,
  updateOrderStateApi,
  // addOrderReceiptApi,
  OrderRequest,
  getOrderStockErrorsApi,
  getPreOrderApi,
  updatePreOrderStateApi,
  updateOrderCollaboratorApi,
  saveCommentApi,
  updateCommentApi,
  deleteCommentApi,
  getOrderCommentsApi,
} from './api';

import {EditOrderSuccess} from '../manual-order/duck';
import {of} from 'rxjs';
import {getOrderTaxInvoiceListByIdsApi} from '../tax-invoice/api';

import type {ActionsObservable} from 'redux-observable';
import type {Action} from '../../store/utils';
import type {ApiError} from '../../http/error';
import type {Order, OrderComment, OrderTaxInvoice} from '../../entities';

export type OrderCommentRequest = {
  comment: string,
};
export type OrderStockError = {
  error: string,
  productId: string,
  productVariantId?: string,
};

export type OrderAction = Action & {
  order?: Order,
  orderId?: string,
  orderRequest?: OrderRequest,
  error?: string,
  forVoucher?: boolean,
  orderCommentRequest?: OrderCommentRequest,
  isPreOrder?: boolean,
  orderTaxInvoice?: OrderTaxInvoice,
};

export type OrderState = {
  order?: Order,
  isFetching?: boolean,
  fetchError?: string,
  orderStockErrors?: OrderStockError[],
  isFetchingOrderStockErrors?: boolean,
  fetchOrderStockErrorsSuccess?: boolean,
  fetchOrderStockErrorsError?: boolean,
  // createOrderError?: ApiError,
  // saveOrderError?: string,
  // editOrderError?: ApiError,
  updateOrderStateSuccess?: OrderComment,
  updateOrderStateError?: ApiError,
  // deleteOrderError?: string,
  orderComments?: OrderComment[],
  orderCommentError?: boolean,
  orderCommentSuccess?: boolean,
  updateOrderCollaboratorError?: boolean,
  updateOrderCollaboratorSuccess?: boolean,

  orderTaxInvoice?: OrderTaxInvoice,
  orderId?: string,
};

export class FetchOrder extends ActionCreator<OrderAction> {}
export class FetchOrderSuccess extends ActionCreator<OrderAction> {}
export class FetchOrderErrorFn extends ActionCreator<OrderAction> {}

export class FetchPreOrder extends ActionCreator<OrderAction> {}
export class FetchPreOrderSuccess extends ActionCreator<OrderAction> {}
export class FetchPreOrderErrorFn extends ActionCreator<OrderAction> {}

export class FetchOrderStockErrors extends ActionCreator<OrderAction> {}
export class FetchOrderStockErrorsSuccess extends ActionCreator<OrderAction> {}
export class FetchOrderStockErrorsError extends ActionCreator<OrderAction> {}

// Update order state actions
export class UpdateOrderState extends ActionCreator<OrderAction> {}
export class UpdateOrderStateSuccess extends ActionCreator<OrderAction> {}
export class UpdateOrderStateError extends ActionCreator<OrderAction> {}
export class UpdatePreOrderState extends ActionCreator<OrderAction> {}
export class UpdatePreOrderStateSuccess extends ActionCreator<OrderAction> {}
export class UpdatePreOrderStateError extends ActionCreator<OrderAction> {}

// order comments actions
export class SaveOrderComment extends ActionCreator<OrderAction> {}
export class SaveOrderCommentSuccess extends ActionCreator<OrderAction> {}
export class SaveOrderCommentError extends ActionCreator<OrderAction> {}
export class FetchOrderComments extends ActionCreator<OrderAction> {}
export class FetchOrderCommentsSuccess extends ActionCreator<OrderAction> {}
export class FetchOrderCommentsErrorFn extends ActionCreator<OrderAction> {}
export class UpdateOrderComment extends ActionCreator<OrderAction> {}
export class UpdateOrderCommentSuccess extends ActionCreator<OrderAction> {}
export class UpdateOrderCommentError extends ActionCreator<OrderAction> {}
export class DeleteOrderComment extends ActionCreator<OrderAction> {}
export class DeleteOrderCommentSuccess extends ActionCreator<OrderAction> {}
export class DeleteOrderCommentError extends ActionCreator<OrderAction> {}

export class UpdateOrderCollaborator extends ActionCreator<OrderAction> {}
export class UpdateOrderCollaboratorSuccess extends ActionCreator<OrderAction> {}
export class UpdateOrderCollaboratorError extends ActionCreator<OrderAction> {}

export class ResetOrderState extends ActionCreator<OrderAction> {}

export class SetOrderTaxInvoice extends ActionCreator<OrderAction> {}

export class SerOrder extends ActionCreator<OrderAction> {}

export class FetchOrderTaxInvoiceData extends ActionCreator<OrderAction> {}
export class FetchOrderTaxInvoiceDataSuccess extends ActionCreator<OrderAction> {}
export class FetchOrderTaxInvoiceDataError extends ActionCreator<OrderAction> {}

export const actions = {
  fetchOrder: (payload: OrderAction) => new FetchOrder(payload),
  fetchOrderStockErrors: (payload: OrderAction) =>
    new FetchOrderStockErrors(payload),
  updateOrderState: (payload: OrderAction) => new UpdateOrderState(payload),
  fetchPreOrder: (payload: OrderAction) => new FetchPreOrder(payload),
  updatePreOrderState: (payload: OrderAction) =>
    new UpdatePreOrderState(payload),

  saveOrderComment: (payload: OrderAction) => new SaveOrderComment(payload),
  fetchOrderComments: (payload: OrderAction) => new FetchOrderComments(payload),
  updateOrderComment: (payload: OrderAction) => new UpdateOrderComment(payload),
  deleteOrderComment: (payload: OrderAction) => new DeleteOrderComment(payload),
  updateOrderCollaborator: (payload: OrderAction) =>
    new UpdateOrderCollaborator(payload),
  resetOrderState: () => new ResetOrderState(),

  setOrder: (order: Order) =>
    new SerOrder({
      order,
    }),

  setOrderTaxInvoice: (orderTaxInvoice: OrderTaxInvoice) =>
    new SetOrderTaxInvoice({
      orderTaxInvoice,
    }),
};

export const initialState = {};

export default createReducer(initialState, {
  [FetchOrder.type]: (state: OrderState, action: OrderAction) => {
    return {
      ...state,
      isFetching: !action.forVoucher,
    };
  },
  [FetchOrderSuccess.type]: (
    state: OrderState,
    {order, orderTaxInvoice}: OrderAction
  ) => ({
    ...state,
    isFetching: undefined,
    order,
    orderTaxInvoice,
  }),
  [FetchOrderErrorFn.type]: (_, {error}: OrderAction) => ({error}),
  [FetchOrderStockErrors.type]: (state: OrderState) => ({
    ...state,
    isFetchingOrderStockErrors: true,
    fetchOrderStockErrorsSuccess: undefined,
    fetchOrderStockErrorsError: undefined,
  }),
  [FetchOrderStockErrorsSuccess.type]: (
    state: OrderState,
    {orderStockErrors}: OrderAction
  ) => ({
    ...state,
    isFetchingOrderStockErrors: undefined,
    fetchOrderStockErrorsSuccess: true,
    orderStockErrors: orderStockErrors,
  }),
  [FetchOrderStockErrorsError.type]: (
    state: OrderState,
    action: OrderAction
  ) => ({
    ...state,
    isFetchingOrderStockErrors: undefined,
    fetchOrderStockErrorsError: action.error,
  }),
  [UpdateOrderState.type]: (state: OrderState, action: OrderAction) => ({
    ...state,
    isSaving: true,
    updateOrderStateError: undefined,
    orderId: action?.orderRequest?.id,
    orderStockErrors: undefined,
    fetchOrderStockErrorsSuccess: undefined,
  }),
  [UpdateOrderStateSuccess.type]: (state: OrderState, {order}: OrderAction) => {
    if (!state.order) {
      return {
        ...state,
        isSaving: undefined,
      };
    }
    return {
      ...state,
      isSaving: undefined,
      updateOrderStateSuccess: order,
      updateOrderStateError: undefined,
      order: merge(state.order, order),
      // orderStockErrors: undefined,
    };
  },
  [UpdateOrderStateError.type]: (state: OrderState, action: OrderAction) => ({
    ...state,
    isSaving: undefined,
    updateOrderStateError: action.error,
    // orderStockErrors: undefined,
  }),
  [EditOrderSuccess.type]: (
    state: ManualOrderState,
    action: ManualOrderAction
  ) => ({
    ...state,
    order: action.order,
  }),
  [FetchPreOrder.type]: (state: OrderState, action: OrderAction) => {
    return {
      ...state,
      isFetching: !action.forVoucher,
    };
  },
  [FetchPreOrderSuccess.type]: (state: OrderState, {order}: OrderAction) => ({
    ...state,
    isFetching: undefined,
    order,
  }),
  [FetchPreOrderErrorFn.type]: (_, {error}: OrderAction) => ({error}),
  [UpdatePreOrderState.type]: (state: OrderState) => ({
    ...state,
    isSaving: true,
    updateOrderStateError: undefined,
    orderStockErrors: undefined,
    fetchOrderStockErrorsSuccess: undefined,
  }),
  [UpdatePreOrderStateSuccess.type]: (
    state: OrderState,
    {order}: OrderAction
  ) => ({
    ...state,
    order,
    isSaving: undefined,
    updateOrderStateSuccess: order,
    updateOrderStateError: undefined,
    // orderStockErrors: undefined,
  }),
  [UpdatePreOrderStateError.type]: (
    state: OrderState,
    action: OrderAction
  ) => ({
    ...state,
    isSaving: undefined,
    updateOrderStateError: action.error,
    // orderStockErrors: undefined,
  }),
  [SaveOrderComment.type]: (state: OrderState) => ({
    ...state,
    isSaving: true,
    orderCommentError: undefined,
    saveOrderCommentSuccess: undefined,
  }),
  [SaveOrderCommentSuccess.type]: (
    state: OrderState,
    {order}: OrderAction
  ) => ({
    ...state,
    isSaving: undefined,
    saveOrderCommentSuccess: true,
  }),
  [SaveOrderCommentError.type]: (state: OrderState, action: OrderAction) => ({
    ...state,
    isSaving: undefined,
    saveOrderCommentError: action.error,
  }),
  [FetchOrderComments.type]: (state: OrderState, action: OrderAction) => {
    return {
      ...state,
      isFetching: true,
      fetchOrderCommentsError: undefined,
    };
  },
  [FetchOrderCommentsSuccess.type]: (
    state: OrderState,
    action: OrderAction
  ) => ({
    ...state,
    isFetching: undefined,
    orderComments: action.data,
  }),
  [FetchOrderCommentsErrorFn.type]: (
    state: OrderState,
    action: OrderAction
  ) => ({
    ...state,
    isFetching: undefined,
    fetchOrderCommentsError: action.error,
  }),
  [UpdateOrderComment.type]: (state: OrderState) => ({
    ...state,
    isSaving: true,
    orderCommentError: undefined,
    updateOrderCommentSuccess: undefined,
  }),
  [UpdateOrderCommentSuccess.type]: (
    state: OrderState,
    {order}: OrderAction
  ) => ({
    ...state,
    isSaving: undefined,
    updateOrderCommentSuccess: true,
  }),
  [UpdateOrderCommentError.type]: (state: OrderState, action: OrderAction) => ({
    ...state,
    isSaving: undefined,
    updateOrderCommentError: action.error,
  }),
  [DeleteOrderComment.type]: (state: OrderState) => ({
    ...state,
    isSaving: true,
    orderCommentError: undefined,
    deleteOrderCommentSuccess: undefined,
  }),
  [DeleteOrderCommentSuccess.type]: (
    state: OrderState,
    {order}: OrderAction
  ) => ({
    ...state,
    isSaving: undefined,
    deleteOrderCommentSuccess: true,
  }),
  [DeleteOrderCommentError.type]: (state: OrderState, action: OrderAction) => ({
    ...state,
    isSaving: undefined,
    deleteOrderCommentError: action.error,
  }),
  [UpdateOrderCollaborator.type]: (state: OrderState) => ({
    ...state,
    isSaving: true,
    updateOrderCollaboratorError: undefined,
    updateOrderCollaboratorSuccess: undefined,
  }),
  [UpdateOrderCollaboratorSuccess.type]: (
    state: OrderState,
    {order}: OrderAction
  ) => ({
    ...state,
    order,
    isSaving: undefined,
    updateOrderCollaboratorSuccess: true,
  }),
  [UpdateOrderCollaboratorError.type]: (
    state: OrderState,
    action: OrderAction
  ): OrderState => ({
    ...state,
    isSaving: undefined,
    updateOrderCollaboratorError: action.error,
  }),
  [ResetOrderState.type]: () => {
    return {
      ...initialState,
    };
  },
  [SerOrder.type]: (state: OrderState, action: OrderAction): OrderState => {
    return {
      ...state,
      order: action.order,
    };
  },
  [SetOrderTaxInvoice.type]: (
    state: OrderState,
    action: OrderAction
  ): OrderState => {
    if (!action.orderTaxInvoice) return state;
    const prevTaxInvoice = state.orderTaxInvoice || {};
    const newTaxInvoice = action.orderTaxInvoice || {};

    return {
      ...state,
      orderTaxInvoice: {
        ...prevTaxInvoice,
        ...newTaxInvoice,
      },
    };
  },
});

export const fetchOrderEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(FetchOrder.type),
    switchMap(({orderId}: OrderAction) => {
      return getOrderApi(undefined, {
        route: buildRoute(endpoints.GET_ORDER, [orderId]),
      }).pipe(
        map((order: Order) => new FetchOrderSuccess({order})),
        catchError(error => of(new FetchOrderErrorFn({error})))
      );
    })
  );

export function fetchOrderTaxInvoiceDataEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(UpdateOrderStateSuccess.type),
    debounceTime(250),
    delay(500),
    mergeMap(() => {
      const order = state$.value.order.order;

      const isInOrderDetailPage = !!order;

      if (!isInOrderDetailPage) {
        return of(new SetOrderTaxInvoice());
      }
      const orderId = state$.value.order?.order?.id;

      return getOrderTaxInvoiceListByIdsApi({order_ids: orderId}).pipe(
        map(taxInvoices => {
          const taxInvoice = taxInvoices && taxInvoices[0];
          if (!taxInvoice) {
            return of(new SetOrderTaxInvoice());
          }

          return new SetOrderTaxInvoice({
            orderTaxInvoice: {taxInvoiceList: taxInvoice.orderTaxInvoices},
          });
        }),
        buildCatchError(FetchOrderTaxInvoiceDataError)
      );
    })
  );
}

export const fetchOrderStockErrorsEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(FetchOrderStockErrors.type),
    switchMap(({orderId, isPreOrder}: OrderAction) => {
      return getOrderStockErrorsApi(undefined, {
        route: buildRoute(endpoints.GET_ORDER_STOCK_ERRORS, [orderId]),
      }).pipe(
        map((orderStockErrors: OrderStockError[]) => {
          if (isPreOrder) {
            return new FetchOrderStockErrorsSuccess();
          }

          return new FetchOrderStockErrorsSuccess({orderStockErrors});
        }),
        buildCatchError(
          FetchOrderStockErrorsError,
          'Error al obtener stock errors'
        )
      );
    })
  );

export function updateOrderStateEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(UpdateOrderState.type),
    switchMap(({orderRequest}: OrderAction) =>
      updateOrderStateApi(
        {
          amountReceived: orderRequest.amountReceived,
          state: orderRequest.state,
        },
        {
          route: buildRoute(endpoints.UPDATE_ORDER_STATE, [orderRequest.id]),
        }
      ).pipe(
        map(
          (order: Order) =>
            new UpdateOrderStateSuccess({
              order: {
                ...order,
                previousState: orderRequest.previousState
                  ? orderRequest.previousState
                  : undefined,
                newState: orderRequest.state,
              },
              orderId: order.id,
            })
        ),
        buildCatchError(UpdateOrderStateError)
      )
    )
  );
}

export const fetchPreOrderEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(FetchPreOrder.type),
    switchMap(({orderId}: OrderAction) => {
      return getPreOrderApi(undefined, {
        route: buildRoute(endpoints.GET_PRE_ORDER, [orderId]),
      }).pipe(
        map((order: Order) => new FetchPreOrderSuccess({order})),
        buildCatchError(FetchPreOrderErrorFn)
      );
    })
  );

export function updatePreOrderStateEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(UpdatePreOrderState.type),
    switchMap(({orderRequest}: OrderAction) =>
      updatePreOrderStateApi(
        {
          amountReceived: orderRequest.amountReceived,
          state: orderRequest.state,
        },
        {
          route: buildRoute(endpoints.UPDATE_PRE_ORDER_STATE, [
            orderRequest.id,
          ]),
        }
      ).pipe(
        map(
          (order: Order) =>
            new UpdatePreOrderStateSuccess({
              order: {
                ...order,
                previousState: orderRequest.previousState
                  ? orderRequest.previousState
                  : undefined,
                newState: orderRequest.state,
              },
              orderId: order.id,
            })
        ),
        buildCatchError(UpdatePreOrderStateError)
      )
    )
  );
}

export function updateOrderCollaboratorEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(UpdateOrderCollaborator.type),
    switchMap(({orderRequest}: OrderAction) =>
      updateOrderCollaboratorApi(
        {
          collaboratorId: orderRequest.collaboratorId,
        },
        {
          route: buildRoute(endpoints.UPDATE_ORDER_COLLABORATOR, [
            orderRequest.id,
          ]),
        }
      ).pipe(
        map(
          (order: Order) =>
            new UpdateOrderCollaboratorSuccess({
              order,
              orderId: order.id,
            })
        ),
        buildCatchError(UpdateOrderCollaboratorError)
      )
    )
  );
}

export function saveOrderCommentEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(SaveOrderComment.type),
    switchMap(({orderCommentRequest}: OrderAction) =>
      saveCommentApi(
        {comment: orderCommentRequest.comment},
        {
          route: buildRoute(endpoints.SAVE_COMMENT, [
            orderCommentRequest.orderId,
          ]),
        }
      ).pipe(
        map(
          comment =>
            new SaveOrderCommentSuccess({
              comment,
            })
        ),
        buildCatchError(SaveOrderCommentError)
      )
    )
  );
}

export const fetchOrderCommentsEpic = (
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    ofType(
      FetchOrderComments.type,
      SaveOrderCommentSuccess.type,
      UpdateOrderCommentSuccess.type,
      DeleteOrderCommentSuccess.type
    ),
    switchMap(({orderId}: OrderAction) => {
      let id;
      if (orderId) id = orderId;
      else id = state$.value.order.order.id;
      return getOrderCommentsApi(undefined, {
        route: buildRoute(endpoints.GET_ORDER_COMMENTS, [id]),
      }).pipe(
        map(result => new FetchOrderCommentsSuccess({data: result.data})),
        buildCatchError(FetchOrderCommentsErrorFn)
      );
    })
  );

export function updateOrderCommentEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(UpdateOrderComment.type),
    switchMap(({orderCommentRequest}: OrderAction) =>
      updateCommentApi(
        {comment: orderCommentRequest.comment},
        {
          route: buildRoute(endpoints.UPDATE_COMMENT, [
            orderCommentRequest.orderId,
            orderCommentRequest.commentId,
          ]),
        }
      ).pipe(
        map(
          comment =>
            new UpdateOrderCommentSuccess({
              comment,
            })
        ),
        buildCatchError(UpdateOrderCommentError)
      )
    )
  );
}

export function deleteOrderCommentEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(DeleteOrderComment.type),
    switchMap(({orderCommentRequest}: OrderAction) =>
      deleteCommentApi({
        route: buildRoute(endpoints.DELETE_COMMENT, [
          orderCommentRequest.orderId,
          orderCommentRequest.commentId,
        ]),
      }).pipe(
        map(
          comment =>
            new DeleteOrderCommentSuccess({
              comment,
            })
        ),
        buildCatchError(DeleteOrderCommentError)
      )
    )
  );
}
